import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaSpinner } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

const DownloadPurchase = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const type = searchParams.get('type');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && type === 'download_all' && sessionId) {
      handleDownload(sessionId);
    } else if (searchParams.get('canceled') === 'true' && type === 'download_all') {
      toast.error('Purchase cancelled.');
    }
  }, [location]);

  const handleDownload = async (sessionId) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/download/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }

      const { downloadUrl } = await response.json();
      
      // Create a temporary link and click it to start the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'all_images.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Your download has started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to start download. Please try again later.');
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase downloads');
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/create-download-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;

    } catch (error) {
      console.error('Download purchase error:', error);
      toast.error(error.message || 'Failed to initiate purchase');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      className="bg-white rounded-xl p-6 shadow-sm border border-black/5"
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h3 className="font-['VT323'] text-xl text-black/80">DOWNLOAD ALL IMAGES</h3>
          <p className="text-black/60 font-['VT323']">
            Get all your images in a single ZIP file
          </p>
          <p className="text-black/40 font-['VT323'] text-sm">
            One-time purchase • Includes future updates
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-['VT323'] text-2xl text-black/80">$5</span>
          <motion.button
            onClick={handlePurchase}
            className="btn btn-green-lg px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoading || !isAuthenticated}
          >
            {isLoading ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <FaDownload className="w-4 h-4" />
                Purchase
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default DownloadPurchase; 
